/**
 * Leave Report Page - Comprehensive leave analytics dashboard
 * Admin and HR only
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { leaveApi } from '../api/leaveApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import type { LeaveReportFilters, TopLeaveEmployee } from '../types/leave.types'

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 5 }, (_, i) => currentYear - i)
const CHART_HEIGHT = 140

const LeaveReportPage = () => {
  const { user } = useAuth()
  const [filters, setFilters] = useState<LeaveReportFilters>({ year: currentYear })
  const [requestsPage, setRequestsPage] = useState(1)

  const hasAccess =
    user?.roles?.some(r => ['admin', 'hr'].includes(r.name.toLowerCase())) ||
    user?.division_name?.toLowerCase().includes('human')

  const { data: reportData, isLoading: isLoadingReport } = useQuery({
    queryKey: ['leaveReport', filters],
    queryFn: () => leaveApi.getComprehensiveReport(filters),
    enabled: hasAccess,
  })

  const { data: employeesData } = useQuery({
    queryKey: ['leaveReportEmployees'],
    queryFn: () => leaveApi.getReportEmployees(),
    enabled: hasAccess,
  })

  const { data: departmentsData } = useQuery({
    queryKey: ['leaveReportDepartments'],
    queryFn: () => leaveApi.getReportDepartments(),
    enabled: hasAccess,
  })

  const { data: leaveTypesData } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: () => leaveApi.getLeaveTypes(),
    enabled: hasAccess,
  })

  const { data: requestsData } = useQuery({
    queryKey: ['leaveReportRequests', filters, requestsPage],
    queryFn: () => leaveApi.getReportRequests({ ...filters, page: requestsPage, limit: 10 }),
    enabled: hasAccess,
  })

  const report = reportData?.data
  const employees = employeesData?.data || []
  const departments = departmentsData?.data || []
  const leaveTypes = leaveTypesData?.data || []
  const requests = requestsData?.data || []
  const requestsPagination = requestsData?.pagination

  const handleFilterChange = (key: keyof LeaveReportFilters, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }))
    setRequestsPage(1)
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-700'
      case 'pending': return 'bg-amber-100 text-amber-700'
      case 'rejected': return 'bg-red-100 text-red-700'
      case 'cancelled': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const getStatusBarColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-500'
      case 'pending': return 'bg-amber-400'
      case 'rejected': return 'bg-red-500'
      default: return 'bg-gray-400'
    }
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to view leave reports.</p>
          <Link to="/leave" className="mt-4 inline-block text-red-600 hover:text-red-700">
            &larr; Back to Leave
          </Link>
        </div>
      </div>
    )
  }

  if (isLoadingReport) return <LoadingPage />

  const maxMonthDays = Math.max(...(report?.monthly_trends.map(t => t.total_days) ?? [0]), 1)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm mb-2">
            <Link to="/leave" className="text-red-100 hover:text-white">Leave</Link>
            <span className="text-red-200">/</span>
            <span className="text-white">Reports</span>
          </nav>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Leave Reports & Analytics</h1>
              <p className="text-red-100 mt-1">Comprehensive overview of leave activities and utilization</p>
            </div>
            <svg className="hidden md:block w-20 h-20 text-red-300 opacity-40" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Filters</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
              <select
                value={filters.year || ''}
                onChange={e => handleFilterChange('year', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
              <select
                value={filters.department || ''}
                onChange={e => handleFilterChange('department', e.target.value || undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Departments</option>
                {departments.map(d => <option key={d.name} value={d.name}>{d.name} ({d.employee_count})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Leave Type</label>
              <select
                value={filters.leave_type_id || ''}
                onChange={e => handleFilterChange('leave_type_id', e.target.value || undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Types</option>
                {leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
              <select
                value={filters.employee_id || ''}
                onChange={e => handleFilterChange('employee_id', e.target.value || undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Employees</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={filters.status || ''}
                onChange={e => handleFilterChange('status', e.target.value || undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Requests</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{report.summary.total_requests}</p>
                <p className="text-xs text-gray-500 mt-1">{report.summary.total_employees_with_leave} employees</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Days Taken</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{report.summary.total_days_taken}</p>
                <p className="text-xs text-gray-500 mt-1">avg {report.summary.average_days_per_request.toFixed(1)}d / request</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Approval Rate</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{report.summary.approval_rate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">{report.summary.approved_requests} approved</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pending</p>
                <p className="text-3xl font-bold text-amber-500 mt-2">{report.summary.pending_requests}</p>
                <p className="text-xs text-gray-500 mt-1">awaiting approval</p>
              </div>
            </div>

            {/* Monthly Trends — stacked bar chart */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Monthly Trends</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Leave days by type per month</p>
                </div>
                {/* Legend */}
                <div className="hidden sm:flex flex-wrap gap-x-4 gap-y-1.5 justify-end max-w-sm">
                  {report.by_type.map(type => (
                    <div key={type.leave_type_id} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: type.leave_type_color }} />
                      <span className="text-xs text-gray-600">{type.leave_type_name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {report.monthly_trends.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-12">No data for this period</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <div className="flex items-end gap-1 min-w-[480px]" style={{ height: `${CHART_HEIGHT + 36}px` }}>
                      {report.monthly_trends.map(trend => {
                        const barH = trend.total_days > 0
                          ? Math.max((trend.total_days / maxMonthDays) * CHART_HEIGHT, 4)
                          : 0
                        return (
                          <div key={trend.month} className="flex-1 flex flex-col items-center min-w-0">
                            <div className="relative w-full" style={{ height: `${CHART_HEIGHT}px` }}>
                              {trend.total_days > 0 && (
                                <div
                                  className="absolute bottom-0 left-0.5 right-0.5 flex flex-col-reverse overflow-hidden rounded-t-sm"
                                  style={{ height: `${barH}px` }}
                                >
                                  {(trend.by_type ?? []).map(type => (
                                    <div
                                      key={type.leave_type_id}
                                      style={{
                                        height: `${(type.days / trend.total_days) * 100}%`,
                                        backgroundColor: type.leave_type_color,
                                        minHeight: '2px',
                                      }}
                                      title={`${type.leave_type_name}: ${type.days}d`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-[10px] font-semibold text-gray-700 mt-1">
                              {trend.total_days > 0 ? trend.total_days : ''}
                            </p>
                            <p className="text-[10px] text-gray-400">{trend.month_name?.substring(0, 3)}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  {/* Mobile legend */}
                  <div className="sm:hidden flex flex-wrap gap-x-3 gap-y-1.5 mt-4">
                    {report.by_type.map(type => (
                      <div key={type.leave_type_id} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: type.leave_type_color }} />
                        <span className="text-xs text-gray-600">{type.leave_type_name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Status Breakdown & Leave Type Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Request Status</h3>
                <div className="space-y-3">
                  {Object.entries(report.by_status).map(([status, data]) => (
                    <div key={status}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm text-gray-600 capitalize">{status}</span>
                        <span className="text-sm font-medium text-gray-700">
                          {data.count} <span className="text-gray-400 font-normal">({data.total_days}d)</span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getStatusBarColor(status)}`}
                          style={{ width: `${data.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">By Leave Type</h3>
                <div className="space-y-3">
                  {report.by_type.map(type => (
                    <div key={type.leave_type_id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: type.leave_type_color }} />
                          <span className="text-sm text-gray-700 truncate">{type.leave_type_name}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-700 ml-2 flex-shrink-0">
                          {type.count} <span className="text-gray-400 font-normal">({type.total_days}d)</span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{ width: `${type.percentage}%`, backgroundColor: type.leave_type_color }}
                        />
                      </div>
                    </div>
                  ))}
                  {report.by_type.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Top Leave Takers & Least Leave Takers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TopLeaveCard employees={report.top_leave_takers.slice(0, 8)} />
              <LeastLeaveCard employees={(report.least_leave_takers ?? []).slice(0, 8)} />
            </div>

            {/* Department Breakdown */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Leave by Department</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wide">Department</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Employees</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Requests</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Days</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Avg Days/Emp</th>
                      <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wide">Approval</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {report.by_department.map(dept => (
                      <tr key={dept.department} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">{dept.department}</td>
                        <td className="py-3 px-4 text-sm text-gray-500 text-right">{dept.employee_count}</td>
                        <td className="py-3 px-4 text-sm text-gray-500 text-right">{dept.total_requests}</td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-700 text-right">{dept.total_days}</td>
                        <td className="py-3 px-4 text-sm text-red-600 text-right font-medium">{dept.average_days_per_employee.toFixed(1)}</td>
                        <td className="py-3 px-6 text-right">
                          <span className={`text-sm font-medium ${
                            dept.approval_rate >= 80 ? 'text-green-600' :
                            dept.approval_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {dept.approval_rate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.by_department.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-10">No department data available</p>
                )}
              </div>
            </div>

            {/* Detailed Requests */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Leave Requests</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Leave Type</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Start</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">End</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wide">Approver</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {requests.map(request => (
                      <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-6">
                          <p className="text-sm font-medium text-gray-900">{request.employee_name}</p>
                          <p className="text-xs text-gray-400">{request.department}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: request.leave_type_color || '#6B7280' }} />
                            <span className="text-sm text-gray-700">{request.leave_type_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 text-center">
                          {request.total_days}d
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 text-center">
                          {new Date(request.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 text-center">
                          {new Date(request.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="py-3 px-6 text-sm text-gray-500">{request.approver_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {requests.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-10">No requests found</p>
                )}
              </div>

              {requestsPagination && requestsPagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    {((requestsPage - 1) * 10) + 1}–{Math.min(requestsPage * 10, requestsPagination.total)} of {requestsPagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setRequestsPage(p => Math.max(1, p - 1))}
                      disabled={requestsPage === 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {requestsPage} / {requestsPagination.total_pages}</span>
                    <button
                      onClick={() => setRequestsPage(p => Math.min(requestsPagination.total_pages, p + 1))}
                      disabled={requestsPage === requestsPagination.total_pages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Top Leave Takers — sick vs other breakdown
const TopLeaveCard = ({ employees }: { employees: TopLeaveEmployee[] }) => {
  const maxDays = Math.max(...employees.map(e => e.approved_days), 1)
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Top Leave Takers</h3>
          <p className="text-xs text-gray-500 mt-0.5">Highest approved leave days</p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-400" />Sick</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" />Other</span>
        </div>
      </div>
      <div className="space-y-3">
        {employees.map((emp, index) => {
          const sick = emp.sick_days ?? 0
          const other = emp.other_days ?? 0
          const sickPct = maxDays > 0 ? (sick / maxDays) * 100 : 0
          const otherPct = maxDays > 0 ? (other / maxDays) * 100 : 0
          return (
            <div key={emp.employee_id} className="flex items-start gap-3">
              <span className="text-sm font-bold w-5 text-center flex-shrink-0 text-red-500 mt-0.5">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between mb-1.5">
                  <p className="text-sm font-medium text-gray-800 truncate">{emp.employee_name}</p>
                  <span className="text-sm font-semibold text-gray-700 ml-2 flex-shrink-0">
                    {emp.approved_days}d
                  </span>
                </div>
                {/* Sick bar */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-gray-400 w-8 flex-shrink-0">Sick</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-red-400 transition-all" style={{ width: `${sickPct}%` }} />
                  </div>
                  <span className="text-[10px] text-red-500 w-5 text-right flex-shrink-0">{sick}d</span>
                </div>
                {/* Other bar */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400 w-8 flex-shrink-0">Other</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${otherPct}%` }} />
                  </div>
                  <span className="text-[10px] text-emerald-600 w-5 text-right flex-shrink-0">{other}d</span>
                </div>
              </div>
            </div>
          )
        })}
        {employees.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No leave data available</p>
        )}
      </div>
    </div>
  )
}

// Least Leave Takers — single bar
const LeastLeaveCard = ({ employees }: { employees: TopLeaveEmployee[] }) => {
  const maxDays = Math.max(...employees.map(e => e.approved_days), 1)
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">Least Leave Takers</h3>
        <p className="text-xs text-gray-500 mt-0.5">Employees with fewest days off</p>
      </div>
      <div className="space-y-3">
        {employees.map((emp, index) => (
          <div key={emp.employee_id} className="flex items-start gap-3">
            <span className="text-sm font-bold w-5 text-center flex-shrink-0 text-blue-500 mt-0.5">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between mb-1.5">
                <p className="text-sm font-medium text-gray-800 truncate">{emp.employee_name}</p>
                <span className="text-sm font-semibold text-gray-700 ml-2 flex-shrink-0">
                  {emp.approved_days}d
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-1.5 rounded-full bg-blue-400 transition-all"
                    style={{ width: `${maxDays > 0 ? (emp.approved_days / maxDays) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0">{emp.total_requests} req</span>
              </div>
            </div>
          </div>
        ))}
        {employees.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No employee data available</p>
        )}
      </div>
    </div>
  )
}

export default LeaveReportPage
